import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import type { DraftMode, Folder, MailAction } from '@jerome2/shared';
import { useDismiss } from '../lib/dismiss.ts';
import { Icon, type IconName } from '../ui/Icon.tsx';

// Rechtsklikmenu op een mail: op de muispositie (of onder een knop: `anchor`), binnen het scherm,
// met een submenu voor Verplaatsen naar…; Esc, klik buiten of scrollen buiten het menu sluit;
// pijltjes en Enter werken. De luisteraars haken één keer aan en lezen de actuele stand uit een ref.
// Een scroll die de klik zelf veroorzaakt, sluit niet (ticket 28625): een rechtsklik kiest de rij, en dan schuift de lijst die
// rij in beeld en begint de lezer bovenaan. Zo'n scroll komt in het eerstvolgende frame, vóór de callbacks van
// requestAnimationFrame; daarom luistert het menu pas daarna naar scrollen. Met het leesvenster onder de lijst staat een rij
// vaak half in beeld, en sloot het menu na een rechtsklik meteen.

export interface MenuItem {
  id: string;
  label: string;
  icon?: IconName | undefined;
  danger?: boolean | undefined;
  disabled?: boolean | undefined;
  children?: MenuItem[] | undefined;
  onSelect?: (() => void) | undefined;
  /** Link: kiezen opent dit adres in een nieuw venster (en roept daarna onSelect aan, als die er is). */
  href?: string | undefined;
  /** Een keuze uit een groep (ticket 28625: de plek van het leesvenster): de regel is dan een menuitemradio met aria-checked, en
   * alleen de gekozen regel heeft een vinkje in plaats van een icoon. */
  checked?: boolean | undefined;
}
export type MenuEntry = MenuItem | 'separator';

/** De rechthoek waaronder een menu hangt; een DOMRect past hierop. */
export interface MenuAnchor {
  top: number;
  left: number;
  right: number;
  bottom: number;
}

/** Voert een gekozen item uit: een link opent in een nieuw venster, daarna onSelect. */
export function runMenuItem(it: MenuItem): void {
  if (it.href) window.open(it.href, '_blank', 'noopener,noreferrer');
  it.onSelect?.();
}

export interface MailMenuTarget {
  flagged: boolean;
  isRead: boolean;
  eodDay?: string | null | undefined;
  parentFolderId: string | null;
  /** Een concept (map Concepten): bewerken en versturen in plaats van beantwoorden. */
  isDraft?: boolean | undefined;
}

/** De vaste menu-items voor een mail, in de volgorde van het ontwerp; een concept krijgt een kort menu. */
export function mailMenuItems(target: MailMenuTarget, folders: Folder[], on: { open: () => void; compose: (mode: DraftMode) => void; action: (action: MailAction, folderId?: string, folderName?: string) => void; eod: (mark: boolean) => void; instruct: () => void; edit?: (() => void) | undefined; send?: (() => void) | undefined }): MenuEntry[] {
  const move: MenuItem[] = folders.filter((f) => f.id !== target.parentFolderId).map((f) => ({ id: `move-${f.id}`, label: f.path, icon: 'folder', onSelect: () => on.action('move', f.id, f.name) }));
  if (target.isDraft) {
    return [
      { id: 'open', label: 'Openen', icon: 'mail', onSelect: on.open },
      { id: 'send', label: 'Verzenden', icon: 'send', onSelect: on.send ?? on.open },
      { id: 'edit', label: 'Bewerken', icon: 'pen', onSelect: on.edit ?? on.open },
      'separator',
      { id: 'delete', label: 'Verwijderen', icon: 'trash', danger: true, onSelect: () => on.action('delete') },
      { id: 'move', label: 'Verplaatsen naar…', icon: 'move', children: move, disabled: !move.length },
    ];
  }
  return [
    { id: 'open', label: 'Openen', icon: 'mail', onSelect: on.open },
    'separator',
    { id: 'reply', label: 'Beantwoorden', icon: 'reply', onSelect: () => on.compose('reply') },
    { id: 'replyAll', label: 'Allen beantwoorden', icon: 'replyAll', onSelect: () => on.compose('replyAll') },
    { id: 'forward', label: 'Doorsturen', icon: 'forward', onSelect: () => on.compose('forward') },
    'separator',
    { id: 'flag', label: target.flagged ? 'Vlag weg' : 'Vlaggen', icon: 'flag', onSelect: () => on.action(target.flagged ? 'unflag' : 'flag') },
    { id: 'read', label: target.isRead ? 'Markeer als ongelezen' : 'Markeer als gelezen', icon: target.isRead ? 'unread' : 'read', onSelect: () => on.action(target.isRead ? 'unread' : 'read') },
    { id: 'archive', label: 'Archiveren', icon: 'archive', onSelect: () => on.action('archive') },
    { id: 'delete', label: 'Verwijderen', icon: 'trash', danger: true, onSelect: () => on.action('delete') },
    { id: 'move', label: 'Verplaatsen naar…', icon: 'move', children: move, disabled: !move.length },
    'separator',
    { id: 'eod', label: target.eodDay ? 'Uit einde dag' : 'Naar einde dag', icon: 'sun', onSelect: () => on.eod(!target.eodDay) },
    { id: 'instruct', label: 'Instructie geven', icon: 'bolt', onSelect: on.instruct },
  ];
}

const MENU_WIDTH = 240;
const ROW = 32;

function place(x: number, y: number, count: number, width = MENU_WIDTH): { left: number; top: number } {
  const w = typeof window === 'undefined' ? 1440 : window.innerWidth;
  const h = typeof window === 'undefined' ? 900 : window.innerHeight;
  const height = count * ROW + 12;
  return { left: Math.max(4, Math.min(x, w - width - 4)), top: Math.max(4, Math.min(y, h - height - 4)) };
}

/** `x`/`y`: de muispositie (rechtsklik). `anchor`: het menu hangt eronder, rechts uitgelijnd met de knop. */
export function ContextMenu(props: { x?: number | undefined; y?: number | undefined; anchor?: MenuAnchor | undefined; items: MenuEntry[]; onClose: () => void; label?: string | undefined }) {
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [focus, setFocus] = useState(0);
  const items = props.items.filter((e): e is MenuItem => e !== 'separator');
  const live = useRef({ items, open, focus, onClose: props.onClose });
  useEffect(() => {
    live.current = { items, open, focus, onClose: props.onClose };
  });

  // Klik buiten sluit; een klik op de knop waaronder het menu hangt niet: die klapt het menu zelf dicht.
  useDismiss(ref, (e) => {
    const a = props.anchor;
    if (a && e instanceof MouseEvent && e.clientX >= a.left && e.clientX <= a.right && e.clientY >= a.top && e.clientY <= a.bottom) return;
    props.onClose();
  });

  useEffect(() => {
    const close = () => live.current.onClose();
    // Vangfase en stopPropagation bij Esc: alleen het menu (of eerst het submenu) gaat dicht, niets eronder.
    const onKey = (e: KeyboardEvent) => {
      const now = live.current;
      const list = now.open ? (now.items.find((i) => i.id === now.open)?.children ?? []) : now.items;
      if (e.key === 'Escape') {
        e.stopPropagation();
        if (now.open) setOpen(null);
        else close();
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setFocus((f) => (list.length ? (f + (e.key === 'ArrowDown' ? 1 : list.length - 1)) % list.length : 0));
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const it = list[now.focus];
        if (!it || it.disabled) return;
        if (it.children) {
          setOpen(it.id);
          setFocus(0);
        } else {
          runMenuItem(it);
          close();
        }
      } else if (e.key === 'ArrowRight' && !now.open) {
        const it = now.items[now.focus];
        if (it?.children?.length) {
          setOpen(it.id);
          setFocus(0);
        }
      } else if (e.key === 'ArrowLeft' && now.open) {
        setFocus(Math.max(0, now.items.findIndex((i) => i.id === now.open)));
        setOpen(null);
      }
    };
    // Scrollen binnen het menu (een lange lijst mappen) laat het staan; scrollen daarbuiten sluit.
    const onScroll = (e: Event) => {
      if (!ref.current?.contains(e.target as Node)) close();
    };
    document.addEventListener('keydown', onKey, true);
    const later = requestAnimationFrame(() => window.addEventListener('scroll', onScroll, true));
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      cancelAnimationFrame(later);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
    };
  }, []);

  const pos = props.anchor ? place(props.anchor.right - MENU_WIDTH, props.anchor.bottom + 4, props.items.length) : place(props.x ?? 0, props.y ?? 0, props.items.length);
  const sub = open ? items.find((i) => i.id === open) : null;
  const subPos = sub ? { left: pos.left + MENU_WIDTH - 8 + MENU_WIDTH > (typeof window === 'undefined' ? 1440 : window.innerWidth) ? -MENU_WIDTH + 8 : MENU_WIDTH - 8, top: Math.max(0, items.indexOf(sub) * ROW - 4) } : null;

  const row = (it: MenuItem, i: number, inSub: boolean) => (
    <button
      key={it.id}
      role={it.checked === undefined ? 'menuitem' : 'menuitemradio'}
      aria-checked={it.checked}
      type="button"
      disabled={it.disabled}
      aria-haspopup={it.children ? 'menu' : undefined}
      aria-expanded={it.children ? open === it.id : undefined}
      className={`w-full flex items-center gap-2.5 h-8 px-3 text-left text-[13px] whitespace-nowrap disabled:opacity-40 ${(inSub ? open : !open) && focus === i ? 'bg-tint' : ''} ${it.danger ? 'text-red hover:bg-red-soft' : 'text-ink hover:bg-tint'}`}
      onMouseEnter={() => {
        setFocus(i);
        if (!inSub) setOpen(it.children?.length ? it.id : null);
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (it.disabled) return;
        if (it.children) {
          setOpen(open === it.id ? null : it.id);
          return;
        }
        runMenuItem(it);
        props.onClose();
      }}
    >
      {it.checked ? <Icon name="check" size={15} className="shrink-0 text-accent" /> : it.icon && it.checked === undefined ? <Icon name={it.icon} size={15} className={`shrink-0 ${it.danger ? 'text-red' : 'text-ink-soft'}`} /> : <span className="w-[15px]" />}
      <span className="truncate flex-1">{it.label}</span>
      {it.children ? <Icon name="chevron" size={13} className="text-muted" /> : null}
    </button>
  );

  // Een rechtsklik in het menu gaat niet door naar wat eronder ligt (de kop van de lezer opent anders zijn eigen menu).
  const onContextMenu = (e: ReactMouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div ref={ref} role="menu" aria-label={props.label ?? 'Acties voor deze mail'} className="fixed z-40 py-1.5 rounded-lg bg-surface border border-line shadow-card" style={{ left: pos.left, top: pos.top, width: MENU_WIDTH }} onContextMenu={onContextMenu}>
      {props.items.map((entry, idx) => (entry === 'separator' ? <div key={`sep-${idx}`} className="my-1 border-t border-line" role="separator" /> : row(entry, items.indexOf(entry), false)))}
      {sub && subPos ? (
        <div role="menu" aria-label={sub.label} className="absolute py-1.5 rounded-lg bg-surface border border-line shadow-card max-h-80 overflow-auto scroll-thin" style={{ left: subPos.left, top: subPos.top, width: MENU_WIDTH }}>
          {(sub.children ?? []).map((c, i) => row(c, i, true))}
        </div>
      ) : null}
    </div>
  );
}
